import { createContext, useContext } from 'react';
import { cn } from '../lib/cn';

/**
 * The wiring `FormField` hands to whatever control sits inside it.
 *
 * This lives apart from `controls.tsx` so that a control in its own file — the
 * date and time pickers — can consume the same id/`aria-describedby` contract
 * without the two files importing each other in a circle.
 */

export interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  /**
   * The label element's own id.
   *
   * `htmlFor` only labels a form element, so a control that is a `div` — the
   * slider's thumb — has to reference the label through `aria-labelledby` or go
   * unnamed. Every control that *is* an input can keep ignoring this.
   */
  labelId: string | undefined;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

export function useFieldContext(): FieldContextValue {
  return (
    useContext(FieldContext) ?? {
      id: '',
      describedBy: undefined,
      invalid: false,
      labelId: undefined,
    }
  );
}

/**
 * The shared shell every text-shaped control wears. Exported so a picker
 * trigger is the *same* border, radius and focus ring as a `TextInput` rather
 * than a copy that drifts the first time one of them is adjusted.
 */
export const controlClasses = cn(
  'w-full rounded-md border bg-surface px-3 text-base text-ink transition-colors',
  'placeholder:text-ink-subtle',
  'focus:outline-none focus:ring-2 focus:ring-brand/30',
  'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-muted',
);
